import dayjs from "dayjs";
import ReactDOMServer from "react-dom/server";

import { shortMonthAndDay, shortMonthAndShortYear } from "@similarweb/pro-logic-components";
import { rgba, colorsPalettes } from "@similarweb/styles";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { displayAnnotation, getAnnotationDateForGranularity } from "app/pages/keyword-analysis/utils/AnnotationBanner";
import type { useGraphTooltip } from "app/pages/keyword-research/keyword-analysis/common-components/clicks-over-time-graph/useGraphTooltip";
import { getIntervalConfig } from "app/pages/website-analysis/traffic-sources/display-ads/overview/graph/helpers/DisplayAdsGraphDataParsers";
import type { BasicDurations } from "app/services/DurationService";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import ChangeBellWithoutTooltip from "pro-features/components/Chart/src/configs/plotLines/ChangeBellWithoutTooltip";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import watermark from "pro-features/components/Chart/src/configs/watermark/watermark";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import type { ValueOf } from "pro-features/types";

import { EGraphGranularities, graphGranularityToString } from "app/common/keyword-research/serp-players/consts";
const dateToUTC = (dateString) => {
    if (dateString) {
        const date = dateString.split("-");
        return Date.UTC(parseInt(date[0], 10), parseInt(date[1], 10) - 1, parseInt(date[2], 10));
    }
};

export const getChartConfig = (
    isStackedShare: boolean,
    timeGranularity: EGraphGranularities,
    is28Days: boolean,
    graphDuration: ValueOf<typeof BasicDurations>,
    tooltip: ReturnType<typeof useGraphTooltip>,
) => {
    const dateForAnnotationDay = swSettings.current?.resources?.VersionRerurnDate__1d6d228;

    const dateForAnnotation = getAnnotationDateForGranularity(
        dateForAnnotationDay,
        graphGranularityToString[EGraphGranularities[EGraphGranularities[timeGranularity]]],
    );

    const format = timeGranularity === EGraphGranularities.MONTHLY ? shortMonthAndShortYear : shortMonthAndDay;
    const yAxisFilter = isStackedShare ? percentageSignFilter : abbrNumberFilter;
    // @ts-expect-error FIXME: functions "percentageSignFilter" and "abbrNumberFilter" have different type of arguments
    const yAxisFormatter = ({ value }) => yAxisFilter()(value, 0);
    const xAxisFormatter = ({ value }) => dayjs.utc(value).format(format);
    const KeywordAnalysisAvailableFromDate = dateToUTC(
        swSettings.components.KeywordAnalysisV2Organic.resources.KeywordAnalysisAvailableFromDate,
    );
    const alertMessageForKeywordAnalysisAvailableFromDate = "keyword.analysis.graph.notification.alert.start.date";

    return combineConfigs({ yAxisFormatter, xAxisFormatter }, [
        noLegendConfig,
        xAxisLabelsConfig,
        xAxisCrosshair,
        tooltipContainerConfig,
        watermark,
        getIntervalConfig(graphGranularityToString[timeGranularity]),
        ChangeBellWithoutTooltip(dayjs.utc(KeywordAnalysisAvailableFromDate), true),
        ChangeBellWithoutTooltip(dayjs(dateForAnnotation), true),

        {
            tooltip: {
                shared: true,
                outside: true,
                useHTML: true,
                formatter: function () {
                    return ReactDOMServer.renderToString(
                        tooltip(
                            this.points[0].point.index,
                            this.x,
                            KeywordAnalysisAvailableFromDate === this.points[0].x &&
                                alertMessageForKeywordAnalysisAvailableFromDate,
                            displayAnnotation(
                                this.points,
                                translate("keyword.research.improved.algorithms.annotation.text"),
                                getAnnotationDateForGranularity(
                                    dateForAnnotationDay,
                                    graphGranularityToString[timeGranularity] as GranularityNames,
                                ),
                            ),
                        ),
                    );
                },
            },
        },
        {
            chart: {
                height: 400,
                spacingTop: 10,
                marginTop: 27,
                marginBottom: 30,
                plotBackgroundColor: "transparent",
                style: {
                    fontFamily: "Roboto",
                },
            },
            plotOptions: {
                area: {
                    marker: {
                        enabled: false,
                    },
                    stacking: "normal",
                    connectNulls: true,
                    fillOpacity: 1,
                    lineWidth: 0.5,
                },
                series: {
                    fillOpacity: 1,
                    animation: false,
                },
            },
            yAxis: [
                {
                    labels: {
                        formatter: yAxisFormatter,
                    },
                    title: {
                        text: null,
                    },
                    gridLineColor: rgba(colorsPalettes.carbon[100], 0.4),
                    gridLineWidth: 1,
                    gridLineDashStyle: "solid",
                    lineWidth: 0,
                    reversed: false,
                    allowDecimals: true,
                    startOnTick: false,
                    endOnTick: true,
                    tickPositioner: null,
                    gridZIndex: 4,
                    showFirstLabel: false,
                    showLastLabel: true,
                    isDirty: true,
                    ...(isStackedShare
                        ? { min: 0, max: 1, tickAmount: 5 }
                        : { min: undefined, max: undefined, tickAmount: undefined }),
                },
                {
                    labels: {
                        enabled: false,
                    },
                    title: {
                        text: null,
                    },
                    gridLineColor: rgba(colorsPalettes.carbon[100], 0.4),
                    gridLineWidth: 1,
                    gridLineDashStyle: "solid",
                    lineWidth: 0,
                    reversed: false,
                    allowDecimals: true,
                    startOnTick: false,
                    endOnTick: true,
                    tickPositioner: null,
                    gridZIndex: 4,
                    showFirstLabel: false,
                    showLastLabel: true,
                    isDirty: true,
                },
            ],
            xAxis: {
                gridLineWidth: 0,
                gridLineDashStyle: "dash",
                labels: {
                    formatter: xAxisFormatter,
                    style: {
                        textTransform: "capitalize",
                        fontSize: "11px",
                        color: colorsPalettes.carbon[250],
                        fontFamily: "Roboto",
                    },
                },
                plotLines: [
                    is28Days && graphDuration !== "28d"
                        ? {
                              color: colorsPalettes.carbon[200],
                              width: 1,
                              dashStyle: "ShortDash",
                              value: dayjs.utc().subtract(28, "days").valueOf(),
                              zIndex: 100,
                              label: {
                                  text: translate("keywordanalysis.organic2.clicks.over.time.graph.plotLine.last28"),
                                  align: "left",
                                  y: 20,
                                  rotation: 0,
                                  style: { color: colorsPalettes.carbon[200] },
                              },
                          }
                        : {},
                ],
                minPadding: 0,
                maxPadding: 0,
                type: "datetime",
                ...(timeGranularity !== EGraphGranularities.DAILY && {
                    tickPositioner: function () {
                        const allXValues: number[] = this.chart.series.flatMap((series) =>
                            (series.points?.map((point) => point.x) ?? series.xData ?? []).filter((x): x is number =>
                                Number.isFinite(x),
                            ),
                        );

                        if (allXValues.length === 0) {
                            return undefined;
                        }

                        const uniqueXValues = [...new Set(allXValues)].sort((a, b) => a - b);
                        if (uniqueXValues.length <= 6) {
                            return undefined;
                        }

                        const skipInterval = uniqueXValues.length > 8 ? 3 : 2;
                        return uniqueXValues.filter((_, i) => i % skipInterval === 0);
                    },
                }),
            },
        },
    ]);
};
